import { ActionTree } from 'vuex';
import {
  ESearchAction,
  ESearchMutation,
  ESearchState,
  state as defaultState,
} from '@/content-library/search/store/search.constants';
import { ISearchState } from '@/content-library/search/store/search.types';
import { EAccessibility } from '@/content-library/search/types/enums/search-types';
import { IAutocompleteSuggestion } from '@/content-library/search/types/search';
import { RootState } from '@/store/store';
import { IResource } from '@/api/resources/types/resource';
import { ResourceService } from '@/api/resources/services/resource.service';
import { ILoadResourcesFilter } from '@/content-library/types/load-resources-filter';
import { EResourceType } from '@/api/resources/types/enum/resource-type';
import { DocumentService } from '@/api/documents/services/document.service';
import { APIErrorMessages } from '@/api/constants/api-errors.constants';
import { IDocument } from '@lib-shared/types/Document';
import { SearchConstants } from '@/content-library/search/constants/search.constants';
import { SearchHelper } from '@/content-library/search/helpers/search.helper';

export const actions: ActionTree<ISearchState, RootState> = {
  async [ESearchAction.LoadAutocomplete](
    { dispatch, commit },
    payload: Partial<ILoadResourcesFilter>
  ) {
    commit(ESearchMutation.SetLoaded, false);
    commit(ESearchMutation.SetLoading, true);
    const { searchTerms, sortBy, sortOrder, type, directory } = payload;
    let resources: IResource[] = [];
    try {
      ({ resources } = await ResourceService.loadList(
        searchTerms,
        SearchConstants.maxAutocompleteSuggestions,
        0,
        sortBy,
        sortOrder,
        type,
        directory
      ));
    } catch {
      dispatch(ESearchAction.LoadAutocompleteFailed);
      return;
    }

    const autocompleteSuggestions: IAutocompleteSuggestion[] = await Promise.all(
      resources.map(async (resource: IResource) => {
        if (resource?.resourceType !== EResourceType.Template) {
          return { resource, accessibility: EAccessibility.Normal };
        }

        try {
          const block: Partial<IDocument> = await DocumentService.loadTemplateResourceDetails(
            resource.contentId
          );
          return {
            resource,
            accessibility: block?.hasWriteAccess
              ? EAccessibility.Normal
              : EAccessibility.AccessDenied,
          };
        } catch (error) {
          return {
            resource,
            accessibility:
              error?.message === APIErrorMessages.NoSuchDocument
                ? EAccessibility.NotFound
                : EAccessibility.OtherError,
          };
        }
      })
    );
    dispatch(ESearchAction.LoadAutocompleteSuccess, autocompleteSuggestions);
  },
  [ESearchAction.LoadAutocompleteSuccess](
    { commit },
    autocompleteSuggestions: IAutocompleteSuggestion[]
  ) {
    commit(ESearchMutation.SetAutocompleteSuggestions, autocompleteSuggestions);
    commit(ESearchMutation.SetLoading, false);
    commit(ESearchMutation.SetLoaded, true);
  },
  [ESearchAction.LoadAutocompleteFailed]({ commit }) {
    commit(ESearchMutation.SetLoaded, false);
    commit(ESearchMutation.SetLoading, false);
  },
  [ESearchAction.AddRecentSearch]({ state, commit }, searchTerm: string) {
    const newRecentSearches: string[] = SearchHelper.addRecentSearchTerm(
      state[ESearchState.RecentSearches],
      searchTerm,
      SearchConstants.maxRecentSearches
    );
    commit(ESearchMutation.SetRecentSearches, newRecentSearches);
  },
  [ESearchAction.RemoveRecentSearch]({ state, commit }, searchTerm: string) {
    const newRecentSearches: string[] = state[ESearchState.RecentSearches].filter(
      term => term.toLowerCase() !== searchTerm.toLowerCase()
    );
    commit(ESearchMutation.SetRecentSearches, newRecentSearches);
  },
  [ESearchAction.ResetAutocompleteSuggestions]({ commit }) {
    commit(ESearchMutation.SetLoaded, false);
    commit(ESearchMutation.SetLoading, false);
    commit(
      ESearchMutation.SetAutocompleteSuggestions,
      defaultState[ESearchState.AutocompleteSuggestions]
    );
  },
};
